import type {
  ComputedDataType,
  ComputedDisplayFormat,
  ComputedEntityType,
  ComputedMetricCategory,
  EventOperation,
  FormulaExpression,
  GrowthComparison,
  ObjectOperation,
  PredicateOperator,
  PredicateType,
} from "../services/computedTraits/computedTraitsEndpoints.interface";

export type ActiveComputedTraitTab = "metrics" | "labels";
export type MetricFormulaType = "event_aggregation" | "event_growth" | "object_aggregation" | "formula";
export type OperandKind = "ref" | "prop" | "raw";
export type RuleMatchType = "all" | "any";

export interface OperandDraft {
  id: string;
  kind: OperandKind;
  value: string;
}

export interface MetricDraft {
  name: string;
  display_name: string;
  description: string;
  entity_type: ComputedEntityType;
  data_type: ComputedDataType;
  display_format: ComputedDisplayFormat;
  category: ComputedMetricCategory;
  compute_priority: number;
  track_history: boolean;
  is_active: boolean;
  formula_type: MetricFormulaType;
  event_names: string;
  event_operation: EventOperation;
  event_days: string;
  event_days_offset: number;
  event_comparison: GrowthComparison;
  object_slug: string;
  object_operation: ObjectOperation;
  object_field: string;
  object_days: string;
  formula_expression: FormulaExpression;
  operands: OperandDraft[];
}

export interface LabelOptionDraft {
  id: string;
  value: string;
  display_name: string;
  color: string;
}

export interface PredicateDraft {
  id: string;
  field: string;
  operator: PredicateOperator;
  type: PredicateType;
  value: string;
}

export interface LabelRuleDraft {
  id: string;
  label: string;
  match_type: RuleMatchType;
  predicates: PredicateDraft[];
}

export interface LabelDraft {
  name: string;
  display_name: string;
  description: string;
  entity_type: ComputedEntityType;
  default_label: string;
  track_history: boolean;
  is_enabled: boolean;
  options: LabelOptionDraft[];
  rules: LabelRuleDraft[];
}

export const ENTITY_TYPE_VALUES: readonly ComputedEntityType[] = ["accounts", "users"];
export const DATA_TYPE_VALUES: readonly ComputedDataType[] = ["int", "float", "datetime", "boolean", "text"];
export const DISPLAY_FORMAT_VALUES: readonly ComputedDisplayFormat[] = [
  "number",
  "percentage",
  "currency",
  "decimal_1",
  "decimal_2",
  "duration_seconds",
];
export const CATEGORY_VALUES: readonly ComputedMetricCategory[] = ["activity", "object", "segmentation"];
export const METRIC_FORMULA_TYPES: readonly MetricFormulaType[] = [
  "event_aggregation",
  "event_growth",
  "object_aggregation",
  "formula",
];
export const GROWTH_COMPARISON_VALUES: readonly GrowthComparison[] = [
  "percentage_change",
  "absolute_change",
  "ratio",
];
export const OPERAND_KIND_VALUES: readonly OperandKind[] = ["ref", "prop", "raw"];
export const RULE_MATCH_VALUES: readonly RuleMatchType[] = ["all", "any"];
export const PREDICATE_TYPE_VALUES: readonly PredicateType[] = ["string", "int", "float", "timestamp", "boolean"];

export const EVENT_OPERATIONS: readonly EventOperation[] = [
  "count",
  "sum",
  "count_distinct_days",
  "count_distinct_users",
  "avg",
  "min",
  "max",
  "first",
  "last",
];

export const OBJECT_OPERATIONS: readonly ObjectOperation[] = [
  "count",
  "sum",
  "avg",
  "min",
  "max",
  "first",
  "last",
];

export const FORMULA_EXPRESSIONS: readonly FormulaExpression[] = [
  "add",
  "subtract",
  "multiply",
  "divide",
  "percentage_change",
  "coalesce",
  "round",
  "abs",
  "ifthen",
];

export const PREDICATE_OPERATORS: readonly PredicateOperator[] = [
  "eq",
  "ne",
  "gt",
  "gte",
  "lt",
  "lte",
  "contain",
  "starts_with",
  "ends_with",
  "does_not_contain",
  "known",
  "unknown",
  "in",
  "range",
  "relative",
  "before",
  "after",
];

export const isOneOf = <T extends string>(value: string | null, allowed: readonly T[]): value is T =>
  value !== null && (allowed as readonly string[]).includes(value);

export const coerceSelectValue = <T extends string>(
  value: string | null,
  allowed: readonly T[],
  fallback: T,
): T => (isOneOf(value, allowed) ? value : fallback);
